/**
 * カートで工事の対応エリアを確かめ、対象外なら決済へ進ませない（2026-08-26・日高さん指示）。
 *
 * ★ なぜカートに置くのか ★
 *   これまでエリア判定は「決済のあと」しか無かった。サンクスページの拡張が
 *   `isSupported: false` を見て「担当者よりメールでご連絡いたします」と出すだけで、
 *   お客様は**払い終えてから**工事に来られないことを知る。
 *   注文側も後追いで「工事エリア外」タグを付けるだけ（order-area-tag.service.ts）。
 *   実測（2026-08-26）: 東京都品川区の注文でカレンダーが出ず、原因調査が必要になった。
 *
 *   トップページの郵便番号バー（aicon-area-bar.js）は任意で、通らずに買える。
 *   商品ページのモーダルの郵便番号ステップは 2026-10-27 に削除した（8/21 から出していなかった）。
 *   ∴ **決済の直前で必ず通る場所**はカートだけ。
 *
 * ★ 止めるのは工事つきのカートだけ（本体のみは Shopify の配送エリアが止める）★
 *   2026-09-12 に運用が変わり、**本体のみ（宅配）も4県限定**になった（日高さん確認）。
 *   それまでは「本体のみは全国へ送れる」前提で、工事の行が無ければ何もしない作りだった。
 *
 *   ★ ここでは本体のみを止めない ★
 *   「本体のみ」バリアントは `requiresShipping: true` で作られている
 *   （backend/src/services/shopify/draft-order.service.ts:16）ため、
 *   **Shopify の配送エリアを4県に絞れば、4県外の住所はチェックアウトで弾かれる**
 *   （公式: 「注文の配送先となる国や地域が、いずれかの配送エリアに含まれており…
 *     必要があります」https://help.shopify.com/ja/manual/shipping/zones ）。
 *   工事・追加工事・延長保証・クリーニングは `requiresShipping: false` なので
 *   配送エリアの影響を受けず、こちらのガードが引き続き必要。
 *   ∴ 二重に止める実装は足さない。**配送エリアの設定が前提**であることに注意。
 *
 * ★ 判定は backend に任せる ★
 *   対応エリアの定義は backend/src/constants/service-areas.ts が唯一の出どころ。
 *   テーマに県名や市町村を書き写すと、エリアを増やしたときに片方だけ古くなる。
 *   セッション不要で読むだけの `/apps/aicon/area/lookup` を使う（aicon-area-bar.js と同じ口）。
 *
 * ★ 確認は必須。ただし障害では止めない（日高さん判断 2026-08-26・案B）★
 *   工事つきのカートでは「決済に進む」を押した時点で横取りしてシートを出し、
 *   **対応エリアと判定できたときだけ**決済へ送る（2026-09-28 までは disabled で止めていた。
 *   嵐さんのデザイン案でシート方式に変更）。未入力のまま決済へ進めると、確認させる意味が無い。
 *
 *   一方、`/apps/aicon/area/lookup` が応答しないときは**通す**。
 *   こちらの障害でお客様の購入を止めるほうが損失が大きく、
 *   エリア外の注文は従来どおり「工事エリア外」タグで拾える
 *   （backend/src/services/shopify/order-area-tag.service.ts）。
 *
 * ★ 行き止まりにしない ★
 *   対象外でも「本体のみなら送れる」ことを併記する。エリアバーと同じ文言に揃える。
 */

/** 標準工事の商品ハンドル。assets/aicon-cart-pair.js の INSTALL_HANDLE と一致必須 */
const INSTALL_HANDLE = 'aicon-standard-install';

/**
 * 工事のみの商品ハンドル（2026-09-01）。
 *
 * ★ ここに足さないとエリア確認が起動しない ★
 * 下の sync() は「工事の行があるか」を handle で見ている。工事のみは本体を伴わない
 * 別商品（backend/src/services/shopify/types.ts の INSTALL_ONLY_PRODUCT_HANDLE）なので、
 * 標準工事の handle だけを見ていると**対応エリア外のお客様が決済まで進めてしまう**。
 * 工事は4県でしか行えないため、本体のみ（宅配・Shopify の配送エリアが弾く）とは扱いが違う。
 *
 * ★ cart-pair.js には足さない ★
 * あちらは「相方のいない工事行を消す」ための一覧で、工事のみは相方を持たないのが正しい。
 * 足すと入れた直後に消える。判定の目的が逆なので、同じ handle でも共有しない。
 */
const INSTALL_ONLY_HANDLE = 'aicon-install-only';

/**
 * クリーニングの商品ハンドル（2026-09-02）。
 *
 * ★ なぜ対象に入れるのか ★
 *   クリーニングもお客様のお宅へ伺う訪問作業で、対応エリアの外なら実施できない。
 *   工事の handle だけを見ていると、**エリア外のお客様がクリーニングだけを
 *   カートに入れて決済まで進めてしまう**（工事のみで踏んだのと同じ穴）。
 *
 * ★ cart-pair.js には足さないこと ★
 *   あちらは「相方のいない行を消す」判定で、足すと入れた直後に消える。
 *   判定の目的が逆なので、同じ handle でも共有しない。
 */
const CLEANING_HANDLE = 'aicon-cleaning';

/** 工事を伴う行か。標準工事（本体とセット）と工事のみのどちらでも郵便番号の確認が要る */
function isInstallLine(line) {
  return (
    line.handle === INSTALL_HANDLE ||
    line.handle === INSTALL_ONLY_HANDLE ||
    line.handle === CLEANING_HANDLE
  );
}

/**
 * ★ 郵便番号はブラウザに残さない。決済のたびに聞く（日高さん指示 2026-10-01）★
 *   以前は確認した郵便番号を localStorage（下のキー）に無期限で保存し、次にカートを開いたとき
 *   自動で照会して、対応エリアならシートを出さずに決済へ進めていた。
 *   ∴ **2回目の注文で別の場所のエアコンを頼むとき、前回の設置先の郵便番号で確認が済んでしまう**。
 *   確認しているのは「今回の設置先」なので、前回の値を使ってはいけない。
 *   同じページの中で確認が済んだあと（シートの「このまま決済に進む」）はそのまま進む。
 *
 * ★ 保存済みの値は読み出さずに消す ★
 *   既に保存されているブラウザに値が残り続けないよう、このファイルが動くたびに消す。
 *   localStorage は設定によって触るだけで例外になるので、失敗しても何もしない。
 *   2026-10 以降にこのキーを使う処理は無いので、しばらくしたらこの削除も外してよい。
 */
const LEGACY_STORAGE_KEY = 'aicon:cart:postal:v1';
try {
  localStorage.removeItem(LEGACY_STORAGE_KEY);
} catch (_e) {
  // 読み書きできない設定のブラウザ。そもそも保存されていない
}

function routeRoot() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}

/** 全角数字・ハイフン・空白を落として7桁だけ残す */
function normalizePostalCode(value) {
  return String(value || '')
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/[^0-9]/g, '')
    .slice(0, 7);
}

/** 「2026-08-31」→「8/31(月)」。エリアバーと同じ見せ方に揃える */
function formatInstallDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!m) return '';
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const week = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()];
  return `${date.getMonth() + 1}/${date.getDate()}(${week})`;
}

/** 決済ボタン。上（snippets/aicon-cart-top-summary.liquid）と下（snippets/cart-summary.liquid）の2つ */
const CHECKOUT_SELECTOR = 'button[name="checkout"], input[name="checkout"]';

class AiconCartAreaGuard extends HTMLElement {
  connectedCallback() {
    this.sheet = this.querySelector('[data-ag-sheet]');
    this.panel = this.querySelector('[data-ag-panel]');
    this.input = this.querySelector('[data-ag-input]');
    this.button = this.querySelector('[data-ag-submit]');
    this.result = this.querySelector('[data-ag-result]');
    this.goWrap = this.querySelector('[data-ag-go-wrap]');
    this.goButton = this.querySelector('[data-ag-go]');
    if (!this.sheet || !this.panel || !this.input || !this.button || !this.result || !this.goWrap || !this.goButton) {
      return;
    }

    this.busy = false;
    /** 対応エリアと判定できた／判定を諦めた＝決済へ進んでよい状態か */
    this.cleared = false;
    /** シートを開くきっかけになった決済ボタン。「このまま決済に進む」でこれを使って送る */
    this.pendingSubmitter = null;

    this.button.addEventListener('click', () => this.lookup());
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (!this.button.disabled) this.lookup();
      }
    });
    // 入力し直したら前の判定を捨てる。古い「対象外」を見ながら別の番号を入れさせない
    this.input.addEventListener('input', () => {
      this.input.value = normalizePostalCode(this.input.value);
      // ★ 7桁そろうまで押せない（デザイン案どおり）★
      this.button.disabled = this.busy || this.input.value.length !== 7;
      if (!this.result.hidden || !this.goWrap.hidden) this.clearResult();
    });
    this.goButton.addEventListener('click', () => this.proceed());
    this.querySelectorAll('[data-ag-close]').forEach((el) => el.addEventListener('click', () => this.close()));

    /*
     * ★ 決済ボタンは disabled にせず、押されたときに横取りする（2026-09-28）★
     *   以前は確認が済むまで disabled にしていた。嵐さんのデザイン案では
     *   「決済に進む」を押すとシートが出るため、ボタンは押せる必要がある。
     *
     *   ★ document の capture 段で拾う ★
     *     テーマはカート更新のたびに決済ボタンごと描き直す（section の morph）。
     *     ボタン自体にリスナーを付けると差し替わった瞬間に外れて素通りできる。
     *     document に1回付ければ、描き直しにも上下2つにも効く。
     *
     *   ★ click と submit の両方で止める ★
     *     click … 普通に押したとき
     *     submit … 数量欄で Enter を押したなど、ボタンを押さずにフォームが送られたとき
     *              （既定のボタン＝決済ボタンが submitter になる）
     */
    this.onClickCapture = (event) => {
      const trigger = event.target.closest?.(CHECKOUT_SELECTOR);
      if (!trigger || !this.shouldBlock()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open(trigger);
    };
    this.onSubmitCapture = (event) => {
      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout' || !this.shouldBlock()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open(submitter);
    };
    this.onKeydown = (event) => {
      if (this.sheet.hidden) return;
      if (event.key === 'Escape') this.close();
      else if (event.key === 'Tab') this.trapFocus(event);
    };
    document.addEventListener('click', this.onClickCapture, true);
    document.addEventListener('submit', this.onSubmitCapture, true);
    document.addEventListener('keydown', this.onKeydown);

    // ★ カートが変わったら見直す ★
    //   工事の行を消せば止める理由が無くなる。逆に後から工事を足すこともある。
    //   イベント名はテーマのもの（assets/standard-events.d.ts の cartLinesUpdate）。
    //   独自の名前にすると誰も発行しない。assets/aicon-cart-pair.js も同じ名前で購読している。
    //
    //   ★ event.promise を待つ ★
    //     このイベントは cart/change を送る**前**に発火する（assets/aicon-cart-pair.js の
    //     onCartLinesUpdate の注記）。すぐ /cart.js を読むと削除前のカートが返り、
    //     工事のみ・クリーニングを消しても「工事あり」のまま止め続ける。
    //     promise を持たない経路（別タブ・他アプリの発火）はその場で読む。
    this.onCartLinesUpdate = (event) => {
      const promise = event?.promise;
      if (!promise || typeof promise.then !== 'function') {
        void this.sync();
        return;
      }
      promise.then(() => this.sync(), () => {});
    };
    document.addEventListener('shopify:cart:lines-update', this.onCartLinesUpdate);
    void this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onClickCapture, true);
    document.removeEventListener('submit', this.onSubmitCapture, true);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.onCartLinesUpdate) {
      document.removeEventListener('shopify:cart:lines-update', this.onCartLinesUpdate);
    }
  }

  /**
   * いま決済を止めるべきか。
   *
   * ★ 工事の行があるかは Liquid が先に決めている ★
   *   data-ag-has-install はサーバーで描いた値なので、`/cart.js` を読み終える前から正しい。
   *   この JS が読み込まれる前の押下は、assets/aicon-cart-area-guard.css の
   *   `:not(:defined)` の規則が決済ボタンを押せなくして塞ぐ（2026-08-26 レビュー指摘の穴）。
   */
  shouldBlock() {
    return this.dataset.agHasInstall === 'true' && !this.cleared;
  }

  /** カートを読み直し、工事つきかどうかを更新する */
  async sync() {
    try {
      const res = await fetch(`${routeRoot()}cart.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const hasInstall = (cart.items || []).some(isInstallLine);
      const wasInstall = this.dataset.agHasInstall === 'true';
      this.dataset.agHasInstall = hasInstall ? 'true' : 'false';

      if (!hasInstall) {
        // ★ 止める理由が無いだけで「確認済み」ではない ★
        //   ここで cleared を立てると、工事を入れ直したときに確認させられない。
        this.cleared = false;
        this.close();
        return;
      }
      // 工事が無い状態から入れ直された＝新しい注文内容。前の確認は引き継がない
      if (!wasInstall) this.cleared = false;
      // 保存済みの郵便番号での先回りの照会はしない（決済のたびに聞く・上の LEGACY_STORAGE_KEY の ★）
    } catch (_e) {
      // カートを読めないときは何もしない。判定は Liquid の値のまま
    }
  }

  /**
   * Tab / Shift+Tab をシートの中で回す（aria-modal なので外へ出さない）。
   * assets/aicon-plan-sheet.js の trap と同じく、移動先を毎回こちらで決める
   * （端の判定だけに頼ると hidden の要素の数え方がずれた瞬間に外へ抜ける）。
   */
  trapFocus(event) {
    const items = [...this.panel.querySelectorAll('button, input, a[href]')].filter(
      (el) => !el.disabled && el.offsetParent !== null,
    );
    // 移動先が1つも無いときは既定の Tab に任せる（止めると操作できなくなる）
    if (!items.length) return;
    event.preventDefault();
    const i = items.indexOf(document.activeElement);
    const next = event.shiftKey ? (i <= 0 ? items.length - 1 : i - 1) : i < 0 || i === items.length - 1 ? 0 : i + 1;
    items[next].focus();
  }

  open(trigger) {
    this.pendingSubmitter = trigger || null;
    this.lastFocused = document.activeElement;
    this.sheet.hidden = false;
    document.body.style.overflow = 'hidden';
    // シートが下から出きってから入力欄へ（CSS のアニメーション 0.25s）
    window.setTimeout(() => {
      if (this.sheet.hidden) return;
      if (!this.goWrap.hidden) this.goButton.focus();
      else this.input.focus();
    }, 250);
  }

  close() {
    if (this.sheet.hidden) return;
    this.sheet.hidden = true;
    document.body.style.overflow = '';
    if (this.lastFocused && typeof this.lastFocused.focus === 'function' && this.lastFocused.isConnected) {
      this.lastFocused.focus();
    }
    this.lastFocused = null;
  }

  /**
   * 「このまま決済に進む」。シートを開いたきっかけの決済ボタンで cart-form を送る。
   *
   * ★ ボタンを click() し直さない ★
   *   押した決済ボタンはカート更新で差し替わっていることがある。
   *   送るのはフォーム（id="cart-form"）で、submitter は生きているときだけ渡す。
   */
  proceed() {
    if (this.shouldBlock()) return;
    this.close();
    const form = document.getElementById('cart-form');
    const submitter =
      this.pendingSubmitter && this.pendingSubmitter.isConnected
        ? this.pendingSubmitter
        : document.querySelector(CHECKOUT_SELECTOR);
    if (form && typeof form.requestSubmit === 'function' && submitter && submitter.form === form) {
      form.requestSubmit(submitter);
      return;
    }
    // requestSubmit が無い古いブラウザ・フォームが見つからないとき
    window.location.href = `${routeRoot()}checkout`;
  }

  setBusy(busy) {
    this.busy = busy;
    this.button.disabled = busy || normalizePostalCode(this.input.value).length !== 7;
    this.button.textContent = busy ? '確認しています…' : '対応エリアを確認する';
  }

  /**
   * 判定を捨てて「未確認」へ戻す。
   *
   * ★ 戻すときは再び止める ★
   *   入力し直している最中は、前の「対応エリアです」を根拠に進ませてはいけない。
   *   番号を1桁変えただけで別の市区町村になることがある。
   */
  clearResult() {
    this.result.textContent = '';
    this.result.hidden = true;
    this.result.className = 'aicon-ag__result';
    this.goWrap.hidden = true;
    this.cleared = false;
  }

  /**
   * エリアを確認できた／確認を諦めた。以後この状態を保つ。
   *
   * 呼ぶのは2箇所だけ:
   *   ・対応エリアと判定できた
   *   ・判定そのものができなかった（案B のフェイルオープン）
   */
  allowCheckout() {
    this.cleared = true;
    this.goWrap.hidden = false;
  }

  async lookup() {
    if (this.busy) return;

    const postalCode = normalizePostalCode(this.input.value);
    if (!/^\d{7}$/.test(postalCode)) {
      // 入力の不備はこちらの障害ではない。確認できるまで止めたままにする
      this.show('error', '郵便番号は7桁の数字でご入力ください。');
      this.cleared = false;
      return;
    }

    this.setBusy(true);
    try {
      const query = new URLSearchParams({ postalCode });
      /*
       * ★ カートの最短工事日は機種ごとに変わる（2026-09-13）★
       *   区分は Liquid が data-ag-lead-category に入れている
       *   （sections/aicon-cart-area-guard.liquid：遅いほうに合わせる／
       *    1台でも INQUIRY ならカート全体が INQUIRY）。
       *   送らないと backend が既定の3営業日で日付を組み立てるため、
       *   7営業日の機種でも「最短3営業日」が出る。
       *   属性が無い古いテーマに当たったときは送らない＝従来どおり既定で動く。
       */
      const leadCategory = this.dataset.agLeadCategory;
      if (leadCategory) query.set('leadCategory', leadCategory);
      const res = await fetch(`${routeRoot()}apps/aicon/area/lookup?${query.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      const body = await res.json().catch(() => null);
      /*
       * ★ 存在しない郵便番号は通さない（2026-09-28）★
       *   以前は backend が「存在しない番号」も「障害」も POSTAL_LOOKUP_FAILED で返していたため、
       *   下の案B（障害なら通す）に落ちて、`0000000` を入れるだけでエリア外のお客様が決済まで進めた。
       *   backend が POSTAL_CODE_NOT_FOUND を分けて返すようにしたので（api/shopify/proxy/areaLookup.ts）、
       *   それと形式不正（INVALID_POSTAL_CODE）は「入れ直してもらう」側にして止めたままにする。
       *   古い backend はこのコードを返さないので、そのときは従来どおり案B に落ちる（悪化はしない）。
       */
      const errorCode = body && body.success === false && body.error ? body.error.code : '';
      if (errorCode === 'POSTAL_CODE_NOT_FOUND' || errorCode === 'INVALID_POSTAL_CODE') {
        /*
         * ★ 行き止まりにしない（2026-09-28 のレビューで検出）★
         *   zipcloud は**事業所の個別郵便番号**を持たない（実測: 東京都庁 163-8001・宮内庁 100-8111 も
         *   「該当なし」）。会社や施設への設置で個別番号を入れた方は、実在する番号なのに止まる。
         *   住所の地域の郵便番号で入れ直せば判定できるので、それを案内し、電話の相談先も添える。
         */
        const tel = document.createElement('p');
        tel.className = 'aicon-ag__resultTel';
        tel.append('ご不明な場合は ');
        const link = document.createElement('a');
        link.href = 'tel:0535339207';
        link.textContent = '053-533-9207';
        tel.append(link, '（平日 9:00〜17:00）');
        this.show(
          'ng',
          'この郵便番号は見つかりませんでした',
          '設置先の郵便番号（7桁）をご確認のうえ、もう一度ご入力ください。会社・施設の個別の郵便番号では確認できないため、住所の地域の郵便番号をご入力ください。',
          tel,
        );
        this.cleared = false;
        this.goWrap.hidden = true;
        return;
      }
      if (!body || body.success !== true) {
        // ★ 案B: 判定できないときは通す ★
        //   backend の不調でお客様の購入を止めるほうが損失が大きい。
        //   エリア外の注文は従来どおり「工事エリア外」タグで拾う。
        this.show(
          'error',
          'ただいま対応エリアを確認できませんでした',
          'そのままお進みいただけます。工事にうかがえない地域だった場合は、担当者よりご連絡いたします。',
        );
        this.allowCheckout();
        return;
      }

      // 郵便番号は保存しない（決済のたびに聞く・上の LEGACY_STORAGE_KEY の ★）
      this.showResult(body.data);
    } catch (e) {
      console.error('[aicon-cart-area-guard]', e);
      this.show(
        'error',
        '通信に失敗しました',
        'そのままお進みいただけます。工事にうかがえない地域だった場合は、担当者よりご連絡いたします。',
      );
      this.allowCheckout();
    } finally {
      this.setBusy(false);
    }
  }

  show(state, text, note, extra) {
    this.result.className = `aicon-ag__result is-${state}`;
    this.result.textContent = '';

    const body = document.createElement('div');
    body.className = 'aicon-ag__resultBody';
    const head = document.createElement('p');
    head.className = 'aicon-ag__resultHead';
    head.textContent = text;
    body.appendChild(head);

    if (note) {
      const sub = document.createElement('p');
      sub.className = 'aicon-ag__resultNote';
      sub.textContent = note;
      body.appendChild(sub);
    }
    if (extra) body.appendChild(extra);
    this.result.appendChild(body);

    // 対応エリアのときは入力欄を畳み、「変更」で戻せるようにする（デザイン案）
    const form = this.querySelector('[data-ag-form]');
    if (form) form.hidden = state === 'ok';
    if (state === 'ok') {
      const change = document.createElement('button');
      change.type = 'button';
      change.className = 'aicon-ag__change';
      change.textContent = '変更';
      change.addEventListener('click', () => {
        this.clearResult();
        if (form) form.hidden = false;
        this.input.focus();
        this.input.select();
      });
      this.result.appendChild(change);
    }
    this.result.hidden = false;
  }

  showResult(data) {
    if (!data.isSupported) {
      // ★ 行き止まりにしない ★ 電話の相談先を添える（フッターと同じ番号・受付時間）
      const tel = document.createElement('p');
      tel.className = 'aicon-ag__resultTel';
      tel.append('ご相談は ');
      const link = document.createElement('a');
      link.href = 'tel:0535339207';
      link.textContent = '053-533-9207';
      tel.append(link, '（平日 9:00〜17:00）');
      this.show(
        'ng',
        `あなたのお住まいの${data.address || '地域'}は対応エリア外です`,
        'エアコン本体のみのご購入も、対応エリア（大阪府・三重県・愛知県・静岡県・神奈川県）への配送に限ります。上記以外の地域へはお届けできません。',
        tel,
      );
      this.cleared = false;
      this.goWrap.hidden = true;
      return;
    }

    /*
     * ★ 在庫問い合わせ（INQUIRY）は日付を出さない ★
     *   backend は leadCategory=INQUIRY のとき earliestDate を返さない
     *   （api/shopify/proxy/areaLookup.ts）。ここで「最短◯日」を書くと、
     *   確認の結果それより遅くなったときに同じ不実表示を作り直す。
     */
    const earliest = data.earliestDate ? formatInstallDate(data.earliestDate) : '';
    // ★ areaName ではなく areaLabel（2026-08-26）★
    //   6 区分体制の areaName は '静岡県①' という社内表記。backend が県名だけにした
    //   areaLabel を返すのでそれを使う。落とし先は zipcloud の住所（日高さん指示 2026-08-28）。
    this.show(
      'ok',
      `${data.areaLabel || data.address} は工事の対応エリアです`,
      earliest ? `最短 ${earliest} に工事できます` : undefined,
    );
    this.allowCheckout();
  }
}

if (!customElements.get('aicon-cart-area-guard')) {
  customElements.define('aicon-cart-area-guard', AiconCartAreaGuard);
}
